import { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  TypoBody, TypoCallout, TypoCaption, TypoFootnote, TypoHeading, TypoHeading2,
} from '@/components/Typography.tsx';
import LADDER_TOURNAMENTS from '@/graphql/queries/ladder-tournaments.graphql';
import LADDER_TOURNAMENT_BRACKET from '@/graphql/queries/ladder-tournament-bracket.graphql';
import LADDER_MY_MATCHES from '@/graphql/queries/ladder-my-matches.graphql';

import { LadderMatch, PublicPlayer, formatDate, formatRelative, getMatchSides, useMyClientId } from './common.ts';
import { AppNote, Badge, EmptyState, InlineLoader, Modal, Panel, PlayerAvatar, Table } from './components.tsx';

interface Tournament {
  tournamentId: number;
  name: string;
  tournamentStatus: 'OPEN' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED';
  isAuto: boolean;
  size: number;
  participantCount: number;
  rounds: number;
  currentRound: number;
  startedAt: string | null;
  completedAt: string | null;
}

interface TournamentEntry {
  seed: number | null;
  isActive: boolean;
  isChampion: boolean;
  finalPlacement: number | null;
  roundReached: number | null;
  joinedAt: string;
  eliminatedAt: string | null;
  tournament: Tournament;
}

interface BracketMatch {
  tournamentRound: number;
  roundName: string;
  bracketSlot: number;
  groupLabel: string | null;
  matchStatus: string | null;
  challengerScore: number;
  opponentScore: number;
  winnerId: number | null;
  challenger: PublicPlayer | null;
  opponent: PublicPlayer | null;
}

const LadderTournaments = () => {
  const { data, loading } = useQuery(LADDER_TOURNAMENTS);
  const [bracketOf, setBracketOf] = useState<Tournament | null>(null);

  if (loading && !data) return <InlineLoader />;

  const current: Tournament | null = data?.current ?? null;
  const open: Tournament[] = (data?.open ?? []).filter((t: Tournament) => t.tournamentId !== current?.tournamentId);
  const history: TournamentEntry[] = data?.history ?? [];
  const currentEntry = history.find(entry => entry.tournament.tournamentId === current?.tournamentId);
  const past = history.filter(entry => !entry.isActive);

  return (
    <div className="flex flex-col gap-6">
      <TypoBody className="text-neutral-text">
        Track your bracket run, browse the lobby and look back at past brackets.{' '}
        <strong className="text-neutral-text-strong">You can only participate in one tournament at a time.</strong>
      </TypoBody>

      {current
        ? <ActiveTournament tournament={current} entry={currentEntry} onViewBracket={() => setBracketOf(current)} />
        : (
          <Panel title="Your active bracket">
            <TypoCallout className="text-neutral2-text-soft">
              You’re not in a tournament right now. Enter one from the lobby in the app.
            </TypoCallout>
          </Panel>
        )}

      <Panel
        title="Tournament lobby"
        action={current && <Badge tone="danger">Finish your active bracket to enter another</Badge>}
      >
        {open.length ? (
          <div className="grid gap-4 cq-lg:grid-cols-2">
            {open.map(tournament => <LobbyCard key={tournament.tournamentId} tournament={tournament} />)}
          </div>
        ) : (
          <TypoCallout className="text-neutral2-text-soft">No tournaments are taking entries right now.</TypoCallout>
        )}
      </Panel>

      <Panel title="Your past brackets">
        {past.length ? (
          <Table
            head={[
              { label: 'Tournament' },
              { label: 'Date' },
              { label: 'Seed', className: 'text-center' },
              { label: 'Result' },
              { label: '', className: 'text-right' },
            ]}
          >
            {past.map(entry => (
              <tr key={entry.tournament.tournamentId} className="hover:bg-shade-5">
                <td className="px-3 py-3 font-semibold text-neutral2-text-strong">{entry.tournament.name}</td>
                <td className="px-3 py-3 whitespace-nowrap text-neutral2-text-softer">
                  {formatDate(entry.tournament.completedAt ?? entry.eliminatedAt ?? entry.joinedAt)}
                </td>
                <td className="px-3 py-3 text-center">{entry.seed ? `#${entry.seed}` : '—'}</td>
                <td className="px-3 py-3"><EntryResult entry={entry} /></td>
                <td className="px-3 py-3 text-right">
                  {entry.tournament.tournamentStatus !== 'CANCELLED' && (
                    <button
                      onClick={() => setBracketOf(entry.tournament)}
                      className="text-callout font-semibold text-primary hover:underline whitespace-nowrap"
                    >
                      View bracket
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        ) : (
          <EmptyState text="No finished tournaments yet." />
        )}
      </Panel>

      <BracketModal tournament={bracketOf} onClose={() => setBracketOf(null)} />
    </div>
  );
};

const ActiveTournament = ({ tournament, entry, onViewBracket }: {
  tournament: Tournament;
  entry?: TournamentEntry;
  onViewBracket: () => void;
}) => {
  const myId = useMyClientId();
  const { data } = useQuery(LADDER_MY_MATCHES, {
    variables: { mode: 'TOURNAMENT', status: 'ACTIVE', limit: 1 },
    skip: tournament.tournamentStatus !== 'ACTIVE',
  });
  const match: LadderMatch | undefined = data?.ladderMyMatches?.[0];
  const isOpen = tournament.tournamentStatus === 'OPEN';

  return (
    <Panel className="flex flex-col gap-5 ring-2 ring-inset ring-success">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <Badge tone="success" className="self-start">
            {isOpen ? 'Registration open' : `Round ${tournament.currentRound} of ${tournament.rounds}`}
          </Badge>
          <TypoHeading2 className="text-neutral2-text-strong font-semibold m:text-heading2-m">
            {tournament.name}
          </TypoHeading2>
          {!isOpen && (
            <button
              onClick={onViewBracket}
              className="self-start text-callout font-semibold text-primary hover:underline"
            >
              View the full {tournament.size}-player bracket →
            </button>
          )}
        </div>
        <div className="text-right">
          <TypoCaption className="block text-neutral2-text-softer">Your seed</TypoCaption>
          <strong className="text-heading2 text-neutral2-text-strong">{entry?.seed ? `#${entry.seed}` : '—'}</strong>
        </div>
      </div>

      {isOpen ? (
        <FillBar count={tournament.participantCount} size={tournament.size} />
      ) : match ? (
        <TournamentMatch match={match} myId={myId} />
      ) : (
        <TypoCallout className="text-neutral2-text-soft">
          Waiting for the next round to be drawn.
        </TypoCallout>
      )}
      <AppNote />
    </Panel>
  );
};

const TournamentMatch = ({ match, myId }: { match: LadderMatch; myId: number }) => {
  const { me, them, myScore, theirScore, isMyTurn } = getMatchSides(match, myId);
  const deadline = formatRelative(match.turnDeadlineAt);

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 p-4 rounded bg-neutral">
      <div className="flex flex-col gap-2">
        <TypoFootnote className="uppercase font-semibold text-neutral-text-softer">
          Current matchup · Round {match.tournamentRound ?? match.currentRound}
        </TypoFootnote>
        <div className="flex flex-wrap items-center gap-3">
          <PlayerChip player={me} highlight />
          <span className="italic text-neutral-text-weak">vs.</span>
          <PlayerChip player={them} />
        </div>
      </div>
      <div className="text-right">
        <strong className="block text-heading2 text-neutral-text-strong">{myScore} – {theirScore}</strong>
        <TypoCaption className={isMyTurn ? 'text-primary font-semibold' : 'text-neutral-text-softer'}>
          {isMyTurn ? `Your turn${deadline ? ` · ends ${deadline}` : ''}` : 'Opponent’s turn'}
        </TypoCaption>
      </div>
    </div>
  );
};

const PlayerChip = ({ player, highlight = false }: { player?: PublicPlayer | null; highlight?: boolean }) => (
  <span
    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-neutral2 font-semibold text-callout
      ${highlight ? 'text-success-neutral-text-softer' : 'text-neutral2-text-strong'}`}
  >
    <PlayerAvatar player={player} size="sm" />
    {player?.ladderName ?? 'TBD'}
  </span>
);

const LobbyCard = ({ tournament }: { tournament: Tournament }) => (
  <div className="flex flex-col gap-4 p-5 rounded bg-neutral">
    <div className="flex items-start justify-between gap-3">
      <div className="flex flex-col gap-1">
        <Badge className="self-start">{tournament.isAuto ? 'Open tournament' : 'Special event'}</Badge>
        <TypoHeading className="text-neutral-text-strong font-semibold">{tournament.name}</TypoHeading>
      </div>
      <TypoCaption className="text-neutral-text-softer whitespace-nowrap">{tournament.rounds} rounds</TypoCaption>
    </div>
    <FillBar count={tournament.participantCount} size={tournament.size} />
  </div>
);

const FillBar = ({ count, size }: { count: number; size: number }) => (
  <div className="flex flex-col gap-1">
    <div className="flex justify-between text-footnote font-semibold">
      <span className="text-neutral-text-soft">Registration</span>
      <span className="text-info-neutral-text-softer">{count} / {size}</span>
    </div>
    <div className="h-2 rounded-full bg-shade-10 overflow-hidden">
      <div
        className="h-full bg-info rounded-full"
        style={{ width: `${size ? Math.min(100, (count / size) * 100) : 0}%` }}
      />
    </div>
  </div>
);

const ORDINAL_SUFFIXES: Record<number, string> = { 1: 'st', 2: 'nd', 3: 'rd' };

const ordinal = (n: number) => {
  const isTeen = n % 100 >= 11 && n % 100 <= 13;
  return `${n}${isTeen ? 'th' : ORDINAL_SUFFIXES[n % 10] ?? 'th'}`;
};

const EntryResult = ({ entry }: { entry: TournamentEntry }) => {
  if (entry.tournament.tournamentStatus === 'CANCELLED') return <Badge>Cancelled</Badge>;
  if (entry.isChampion) return <Badge tone="warning">🏆 Champion</Badge>;
  if (entry.finalPlacement) return <Badge tone="info">{ordinal(entry.finalPlacement)} place</Badge>;
  if (entry.roundReached) {
    return (
      <TypoCallout className="text-neutral2-text-soft">
        Out in round {entry.roundReached} of {entry.tournament.rounds}
      </TypoCallout>
    );
  }
  return <TypoCallout className="text-neutral2-text-weak">—</TypoCallout>;
};

const BracketModal = ({ tournament, onClose }: { tournament: Tournament | null; onClose: () => void }) => {
  const myId = useMyClientId();
  const { data, loading } = useQuery(LADDER_TOURNAMENT_BRACKET, {
    variables: { tournamentId: tournament?.tournamentId },
    skip: !tournament,
  });
  const matches: BracketMatch[] = data?.ladderTournamentBracket ?? [];
  const columns = buildBracketColumns(matches, tournament?.rounds ?? 0);

  return (
    <Modal open={Boolean(tournament)} title={tournament?.name ?? 'Bracket'} onClose={onClose} wide fit>
      {loading ? <InlineLoader /> : !matches.length ? (
        <TypoCallout className="text-neutral-text-soft">The bracket hasn’t been drawn yet.</TypoCallout>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-2">
          {columns.map(({ round, name, slots }) => (
            <div key={round} className="flex flex-col gap-3 w-56 shrink-0">
              <TypoFootnote className="uppercase font-semibold text-neutral-text-strong">{name}</TypoFootnote>
              {/* Every slot takes an equal share of the column height, so each match sits centred between
                  the two matches that feed into it from the previous round. */}
              <div className="flex flex-col flex-1 gap-3">
                {slots.map((match, index) => (
                  <div key={match?.bracketSlot ?? `empty-${index}`} className="flex flex-col justify-center flex-1">
                    <BracketCard match={match} myId={myId} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

interface BracketColumn {
  round: number;
  name: string;
  slots: (BracketMatch | null)[];
}

/**
 * Lays the matches out as a single-elimination bracket: each round has half the slots of the one before it,
 * and rounds that haven't been drawn yet are padded with empty slots so the later columns still line up.
 */
const buildBracketColumns = (matches: BracketMatch[], totalRounds: number): BracketColumn[] => {
  const drawnRounds = Array.from(new Set(matches.map(m => m.tournamentRound))).sort((a, b) => a - b);
  if (!drawnRounds.length) return [];

  const firstRound = drawnRounds[0];
  const lastRound = Math.max(drawnRounds[drawnRounds.length - 1], firstRound + totalRounds - 1);
  const firstRoundCount = matches.filter(m => m.tournamentRound === firstRound).length;
  // Slots may be numbered from 0 or 1 within each round; the first round is always fully drawn.
  const slotBase = Math.min(...matches.filter(m => m.tournamentRound === firstRound).map(m => m.bracketSlot));

  const columns: BracketColumn[] = [];
  for (let round = firstRound; round <= lastRound; round++) {
    const roundMatches = matches
      .filter(m => m.tournamentRound === round)
      .sort((a, b) => a.bracketSlot - b.bracketSlot);
    const size = Math.max(Math.ceil(firstRoundCount / 2 ** (round - firstRound)), roundMatches.length, 1);
    const slots: (BracketMatch | null)[] = Array(size).fill(null);
    const slotted = roundMatches.every(m => m.bracketSlot - slotBase >= 0 && m.bracketSlot - slotBase < size);

    roundMatches.forEach((match, index) => {
      slots[slotted ? match.bracketSlot - slotBase : index] = match;
    });

    columns.push({ round, name: roundMatches[0]?.roundName ?? `Round ${round}`, slots });
  }

  return columns;
};

const BracketCard = ({ match, myId }: { match: BracketMatch | null; myId: number }) => (
  <div className={`flex flex-col rounded-sm bg-neutral2 overflow-hidden ${match ? '' : 'opacity-60'}`}>
    {match?.groupLabel && (
      <TypoCaption className="px-3 pt-1.5 text-neutral2-text-weak">{match.groupLabel}</TypoCaption>
    )}
    <BracketSide
      player={match?.challenger ?? null}
      score={match?.challengerScore}
      won={Boolean(match?.winnerId) && match?.winnerId === match?.challenger?.clientId}
      isMe={Boolean(match?.challenger) && match?.challenger?.clientId === myId}
    />
    <BracketSide
      player={match?.opponent ?? null}
      score={match?.opponentScore}
      won={Boolean(match?.winnerId) && match?.winnerId === match?.opponent?.clientId}
      isMe={Boolean(match?.opponent) && match?.opponent?.clientId === myId}
    />
  </div>
);

const BracketSide = ({ player, score, won, isMe }: {
  player: PublicPlayer | null;
  score?: number;
  won: boolean;
  isMe: boolean;
}) => (
  <div className={`flex items-center justify-between gap-2 px-3 py-1.5 text-callout ${isMe ? 'bg-neutral3' : ''}`}>
    <span className={`truncate ${won ? 'font-bold text-neutral2-text-strong' : 'text-neutral2-text-soft'}`}>
      {player?.ladderName ?? 'TBD'}
    </span>
    <span className={won ? 'font-bold text-success-neutral-text-softer' : 'text-neutral2-text-softer'}>{score}</span>
  </div>
);

export default LadderTournaments;
